import { dispatchCartFetch } from '@theme/seal-dispatch-snapshot';
import { CartAddEvent } from '@theme/events';

const RESET_DELAY = 1800;

class SealProductUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick = async (event) => {
    const detailsButton = event.target?.closest?.('[data-seal-product-upsell-details]');
    const quickviewTrigger = event.target?.closest?.('[data-seal-product-upsell-quickview]');
    const card = event.target?.closest?.('[data-seal-product-upsell-card]');
    const clickedButton = event.target?.closest?.('[data-seal-product-upsell-add]');

    if (
      (detailsButton && this.contains(detailsButton)) ||
      (quickviewTrigger && this.contains(quickviewTrigger))
    ) {
      return;
    }

    if (!card && !clickedButton) return;

    const button = clickedButton && this.contains(clickedButton)
      ? clickedButton
      : this.querySelector('[data-seal-product-upsell-add]');
    if (!button || button.disabled) return;

    event.preventDefault();

    const variantId = button.dataset.variantId || this.dataset.variantId;
    if (!variantId) return;

    this.setState('loading');

    try {
      const response = await dispatchCartFetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
            },
          ],
          sections: this.getCartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Cart add failed');
      }

      const cart = await this.fetchCart();

      this.animateFlyToCart();
      this.setState('added');

      window.setTimeout(() => {
        this.dispatchEvent(
          new CartAddEvent(cart ?? data, `seal-product-upsell-${variantId}`, {
            source: cart ? 'seal-product-upsell' : 'product-form-component',
            itemCount: cart?.item_count ?? 1,
            variantId,
            sections: data.sections,
            openCartDrawer: false,
          })
        );
      }, 180);

      window.setTimeout(() => {
        this.setState('idle');
      }, RESET_DELAY);
    } catch (error) {
      console.error(error);
      this.setState('error');

      window.setTimeout(() => {
        this.setState('idle');
      }, RESET_DELAY);
    }
  };

  getCartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js', {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) return null;

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      return null;
    }
  }

  animateFlyToCart() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const source = this.querySelector('.seal-product-upsell__image');
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (!source || !cartIcon || !customElements.get('fly-to-cart')) return;

    const imageUrl = source.currentSrc || source.src;
    if (!imageUrl) return;

    const flyToCartElement = document.createElement('fly-to-cart');

    flyToCartElement.classList.add('fly-to-cart--quick');
    flyToCartElement.style.setProperty('background-image', `url(${imageUrl})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = source;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  setState(state) {
    this.classList.toggle('is-loading', state === 'loading');
    this.classList.toggle('is-added', state === 'added');
    this.classList.toggle('is-error', state === 'error');

    this.querySelectorAll('[data-seal-product-upsell-add]').forEach((button) => {
      const liveLabel = button.querySelector('.visually-hidden');
      const defaultLabel = button.dataset.label || '';
      const addedLabel = button.dataset.addedLabel || defaultLabel;
      const errorLabel = button.dataset.errorLabel || defaultLabel;

      button.classList.toggle('is-loading', state === 'loading');
      button.classList.toggle('is-added', state === 'added');
      button.classList.toggle('is-error', state === 'error');
      button.disabled = state === 'loading' || state === 'added';

      if (!liveLabel) return;

      if (state === 'added') {
        liveLabel.textContent = addedLabel;
      } else if (state === 'error') {
        liveLabel.textContent = errorLabel;
      } else {
        liveLabel.textContent = defaultLabel;
      }
    });
  }
}

if (!customElements.get('seal-product-upsell')) {
  customElements.define('seal-product-upsell', SealProductUpsell);
}
